import { ColorBrandBlue1, ColorNeutralDarkGrey, Spacing16, Spacing4 } from 'autogen/design-tokens/tokens';
import { Disable, Enable, type PersonalOfferListItem } from 'autogen/swagger/all';
import Card from 'components/generic/card';
import ControlledToggleButton from 'components/generic/Toggle/components/ControlledToggleButton';
import { Typography } from 'components/generic/typography';
import React, { useState } from 'react';
import styled from 'styled-components';
import HighlightedText from 'components/generic/HighlightedText';
import PinCode from './pinCode';
import { updateBasket } from '../shared/basket';
import toast from 'components/Toast';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { useTranslation } from 'react-i18next';
import format from 'helpers/translations';

const StyledCard = styled(Card)<{ isActive: boolean }>`
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.ColorBorderFocus};
`;

const StyledCardContent = styled(Card.Content)`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.Spacing16};
`;

const Content = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Label = styled(Typography.Label)`
    margin-bottom: ${({ theme }) => theme.Spacing4};
`;

const StyledTitle = styled(Typography.Title)`
    margin-bottom: ${({ theme }) => theme.Spacing4};
`;

const InfoText = styled(Typography.Body)`
    display: inline;
    margin-bottom: 0;
`;

const FadedText = styled(Typography.Body)`
    display: inline;
    margin-bottom: 0;
    color: ${({ theme }) => theme.ColorTextSecondary};
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ToggleContainer = styled.div`
    align-self: center;
    margin-left: ${({ theme }) => theme.Spacing16};
`;

const StyledHighlightedText = styled(HighlightedText)`
    padding: 0;
`;

export default function OfferItem(offer: PersonalOfferListItem): JSX.Element {
    const { title, type, isEnabled, pinCodeText, requirePinCode, discountId, inactiveReason, isUsed, infoText } = offer;
    const fadedText = [pinCodeText?.header].join(' ').trim();
    const [isActive, setIsActive] = useState<boolean>(isEnabled);
    const [pinCodeApproved, setPinCodeApproved] = useState<boolean>(!requirePinCode);
    const { t } = useTranslation();

    function toggleHandler() {
        const enabled = !isActive;
        // optimistic state handling
        setIsActive(enabled);

        if (enabled) {
            Enable({ discountId })
                .then(() => {
                    if (pinCodeApproved) {
                        updateBasket();
                    }
                })
                .catch(() => {
                    setIsActive(!enabled);
                    toast.error(t(GeneralExceptionError));
                });
        } else {
            Disable({ discountId })
                .then(() => {
                    updateBasket();
                })
                .catch(() => {
                    setIsActive(!enabled);
                    toast.error(t(GeneralExceptionError));
                });
        }
    }

    return (
        <StyledCard size="md" isActive={isActive} onClick={toggleHandler}>
            <StyledCardContent>
                <Content>
                    <InfoContainer>
                        <Label size="S">{type}</Label>
                        <StyledTitle size="S">{title}</StyledTitle>
                        {infoText && <InfoText size="S">{format(infoText)}</InfoText>}
                        {fadedText && <FadedText size="S">{format(fadedText)}</FadedText>}
                    </InfoContainer>
                    <ToggleContainer>
                        <ControlledToggleButton active={isActive} readOnly />
                    </ToggleContainer>
                </Content>
                {inactiveReason && (
                    <StyledHighlightedText color="ColorSpot1" withBorder={false}>
                        {inactiveReason}
                    </StyledHighlightedText>
                )}
                {isActive && requirePinCode && pinCodeText && (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                    <div onClick={(event) => event.stopPropagation()} role="presentation">
                        <PinCode
                            {...pinCodeText}
                            onSubmitCallback={(success) => {
                                setPinCodeApproved(success);
                                if (success) {
                                    updateBasket();
                                }
                            }}
                        />
                    </div>
                )}
            </StyledCardContent>
        </StyledCard>
    );
}
